class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.intervalId = null;
  }

  connectedCallback() {
    this.mode = this.dataset.mode || 'fixed_date';
    this.timezone = this.dataset.timezone || 'Europe/Paris';
    this.endDate = this.dataset.endDate;
    this.dailyDuration = parseInt(this.dataset.dailyDuration) || 24;

    // Cache DOM references
    this.daysEl = this.querySelector('[data-days]');
    this.hoursEl = this.querySelector('[data-hours]');
    this.minutesEl = this.querySelector('[data-minutes]');
    this.secondsEl = this.querySelector('[data-seconds]');

    this.initCountdown();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  initCountdown() {
    this.cleanup();
    this.calculateTargetTime();

    if (!this.targetTime) {
      this.handleExpiry();
      return;
    }

    this.updateDisplay();
    this.intervalId = setInterval(() => this.updateDisplay(), 1000);
  }

  calculateTargetTime() {
    if (this.mode === 'daily_reset') {
      this.targetTime = this.getDailyResetTarget();
    } else {
      this.targetTime = this.parseEndDate(this.endDate);
    }
  }

  getDailyResetTarget() {
    const now = new Date();
    const durationMs = this.dailyDuration * 60 * 60 * 1000;

    // Get current time in Paris timezone
    const parisFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: this.timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const parisParts = parisFormatter.formatToParts(now);
    const parisValues = {};
    parisParts.forEach((part) => {
      parisValues[part.type] = part.value;
    });

    // Create midnight Paris time for today
    const todayMidnightParis = new Date(
      `${parisValues.year}-${parisValues.month}-${parisValues.day}T00:00:00`
    );

    // Calculate offset to get correct UTC time
    const parisOffset = this.getTimezoneOffset(this.timezone, todayMidnightParis);
    const midnightUtc = todayMidnightParis.getTime() - parisOffset;

    // Target is midnight + duration (e.g., midnight + 6h = 06:00 Paris)
    let targetTime = midnightUtc + durationMs;

    // If target has passed, set to tomorrow's target
    if (now.getTime() >= targetTime) {
      targetTime += 24 * 60 * 60 * 1000; // Add 24 hours
    }

    return targetTime;
  }

  getTimezoneOffset(tz, date) {
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: tz }));
    return tzDate.getTime() - utcDate.getTime();
  }

  parseEndDate(dateString) {
    if (!dateString) return null;

    // Support formats: YYYY-MM-DD HH:MM or YYYY-MM-DDTHH:MM
    const normalized = dateString.replace(' ', 'T');

    // If no timezone info, treat as Paris time
    if (
      !normalized.includes('Z') &&
      !normalized.includes('+') &&
      !normalized.match(/T\d{2}:\d{2}.*-/)
    ) {
      // Parse as Paris timezone
      const localDate = new Date(normalized);
      if (isNaN(localDate.getTime())) return null;

      const parisOffset = this.getTimezoneOffset(this.timezone, localDate);
      return localDate.getTime() - parisOffset;
    }

    const date = new Date(normalized);
    return isNaN(date.getTime()) ? null : date.getTime();
  }

  updateDisplay() {
    const now = Date.now();
    let diff = this.targetTime - now;

    // Check if countdown has expired
    if (diff <= 0) {
      if (this.mode === 'daily_reset') {
        // Reset for next day
        this.calculateTargetTime();
        diff = this.targetTime - now;
      } else {
        // Fixed date expired - hide countdown
        this.handleExpiry();
        return;
      }
    }

    // Calculate time units
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    // Update DOM with animation
    this.updateValue(this.daysEl, days);
    this.updateValue(this.hoursEl, hours);
    this.updateValue(this.minutesEl, minutes);
    this.updateValue(this.secondsEl, seconds);

    // Add urgency class when under 1 hour
    if (diff < 3600000) {
      this.classList.add('announcement-countdown--urgent');
    } else {
      this.classList.remove('announcement-countdown--urgent');
    }
  }

  updateValue(element, value) {
    if (!element) return;

    const formatted = value.toString().padStart(2, '0');
    const currentValue = element.textContent;

    if (currentValue !== formatted) {
      element.textContent = formatted;
      // Trigger flip animation
      element.classList.add('announcement-countdown__value--flip');
      setTimeout(
        () => element.classList.remove('announcement-countdown__value--flip'),
        300
      );
    }
  }

  handleExpiry() {
    this.cleanup();
    // Hide the countdown, but keep text visible
    this.style.display = 'none';
    this.setAttribute('data-expired', 'true');
    // Dispatch custom event for potential hooks
    this.dispatchEvent(new CustomEvent('countdown:expired', { bubbles: true }));
  }
}

// Only define if not already defined (for hot reload support)
if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
